import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Solo un 401 o un 403 quieren decir que la sesion no sirve. Antes cualquier
// error de /auth/me (tambien el 520 de Emergent mientras publica, o un corte de
// red) borraba el token y sacaba al usuario (revision del 26-oct, E.11).
const sesionInvalida = (error) => [401, 403].includes(error?.response?.status);
// Los 401 de backend/auth.py: la sesion ya no sirve.
const SESION_TERMINADA = new Set([
  'Token expired', 'Invalid token', 'User not found', 'Your password changed. Sign in again.',
]);
const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const REINTENTOS_MS = [0, 2000, 4000, 8000];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        for (const espera of REINTENTOS_MS) {
          if (espera) await esperar(espera);
          try {
            const response = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
            setToken(storedToken);
            break;
          } catch (error) {
            if (sesionInvalida(error)) {
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
              break;
            }
            // Otro error: se reintenta. Si no contesta, el token queda guardado
            // y al recargar la pagina la sesion vuelve sola.
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Otra pestana cambio la contrasena (token nuevo), entro con otra cuenta o
  // cerro la sesion: esta la sigue, en vez de quedarse con un token que da 401.
  useEffect(() => {
    const alCambiar = (e) => {
      if (e.key !== 'token') return;
      if (!e.newValue) {
        setToken(null);
        setUser(null);
        return;
      }
      setToken(e.newValue);
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${e.newValue}` } })
        .then((r) => setUser(r.data))
        // Sin respuesta no se sabe de quien es el token nuevo: se recarga y el
        // arranque (con sus reintentos) lo resuelve, en vez de mezclar cuentas.
        .catch(() => window.location.reload());
    };
    window.addEventListener('storage', alCambiar);
    return () => window.removeEventListener('storage', alCambiar);
  }, []);

  // Una sesion cerrada (contrasena nueva en otro dispositivo) o vencida se sale
  // al login en vez de dejar cada pantalla en error. Solo con los 401 de sesion:
  // WebShip, Shopify o ShipStation tambien contestan 401 cuando rechazan su llave.
  useEffect(() => {
    const id = axios.interceptors.response.use(undefined, (error) => {
      if (error?.response?.status === 401 && SESION_TERMINADA.has(error.response.data?.detail)
          && localStorage.getItem('token')) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      return Promise.reject(error);
    });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // MFA challenge — caller must run verifyMfa() next
    if (response.data.mfa_required) {
      return { mfa_required: true, ...response.data };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verifyMfa = async (challengeToken, code) => {
    const response = await axios.post(`${API}/mfa/verify`, {
      challenge_token: challengeToken,
      code,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, companyName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      company_name: companyName
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Al cambiar la contrasena el servidor cierra las otras sesiones y le manda
  // a esta un token nuevo (entrega 54).
  const actualizarToken = (nuevo) => {
    localStorage.setItem('token', nuevo);
    setToken(nuevo);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const value = {
    user,
    token,
    loading,
    login,
    verifyMfa,
    register,
    logout,
    actualizarToken,
    getAuthHeaders,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
